<script lang="ts">
  import { get } from "svelte/store";
  import {
    appConfig,
    session,
    filters,
    selectedDay,
    logout,
    isPhone,
    DAY_VIEWS,
  } from "../lib/stores";
  import type { ViewKind } from "../lib/stores";
  import { t } from "../lib/i18n";
  import { publishHeight } from "../lib/stickyOffset";
  import DayModal from "./DayModal.svelte";
  import CalendarView from "./CalendarView.svelte";
  import TimelineView from "./TimelineView.svelte";
  import EventsView from "./EventsView.svelte";
  import Sidebar from "./Sidebar.svelte";
  import Controls from "./Controls.svelte";
  import VotingsPanel from "./VotingsPanel.svelte";
  import AdminPanel from "./AdminPanel.svelte";
  import AccountSheet from "./AccountSheet.svelte";
  import NavIcon from "./NavIcon.svelte";
  import LanguagePicker from "./LanguagePicker.svelte";
  import ThemePicker from "./ThemePicker.svelte";
  import Logo from "./Logo.svelte";

  let adminOpen = $state(false);
  let accountOpen = $state(false);

  const TABS: { view: ViewKind; key: string }[] = [
    { view: "calendar", key: "tab.calendar" },
    { view: "timeline", key: "tab.timeline" },
    { view: "events", key: "tab.events" },
    // phone only — on wide layouts the votings rail is always on screen
    { view: "votings", key: "tab.votings" },
  ];
  const wideTabs = TABS.filter((tab) => tab.view !== "votings");

  function setView(view: ViewKind) {
    filters.update((f) => ({ ...f, view }));
    // a tab switch is a new screen: start at the top of it, not wherever the
    // previous (infinitely long) view happened to be scrolled to
    if (get(isPhone)) window.scrollTo({ top: 0 });
  }

  /** Rotating the phone into a tablet layout must not strand the user on a view
   * that layout does not have. */
  $effect(() => {
    if (!$isPhone && $filters.view === "votings") {
      filters.update((f) => ({ ...f, view: "calendar" }));
    }
  });

  const initial = $derived(($session?.name ?? "?").trim().charAt(0).toUpperCase());

  async function onSignOut() {
    await logout();
  }
</script>

<div class="shell" class:phone={$isPhone}>
  <!-- publishes --topbar-h so the sticky month/year headings further down can
       park themselves just below this bar instead of sliding under it -->
  <header class="topbar panel" use:publishHeight={"--topbar-h"}>
    <div class="brand">
      <Logo size={$isPhone ? 28 : 32} />
      <span class="name">{$t("app.title", { owner: $appConfig?.ownerName ?? "" })}</span>
    </div>

    {#if $isPhone}
      <!-- one button instead of five controls: everything behind it lives in
           the account sheet, which has room for real tap targets -->
      <button
        class="account-btn"
        onclick={() => (accountOpen = true)}
        aria-haspopup="dialog"
        aria-label={$t("account.open")}
        title={$t("account.open")}
      >
        {#if $session?.picture}
          <img class="avatar" src={$session.picture} alt="" referrerpolicy="no-referrer" />
        {:else}
          <span class="initial" aria-hidden="true">{initial}</span>
        {/if}
      </button>
    {:else}
      <div class="session">
        <LanguagePicker />
        <ThemePicker />

        {#if $session}
          {#if $session.picture}
            <img class="avatar" src={$session.picture} alt="" referrerpolicy="no-referrer" />
          {/if}
          <span class="who"
            >{$session.name}{$session.role === "admin" ? ` · ${$t("topbar.admin")}` : ""}</span
          >
          {#if $session.role === "admin"}
            <button class="btn" onclick={() => (adminOpen = true)}>{$t("topbar.manage")}</button>
          {/if}
          <button class="btn secondary" onclick={onSignOut}>{$t("topbar.signout")}</button>
        {/if}
      </div>
    {/if}
  </header>

  {#if accountOpen}
    <AccountSheet
      onClose={() => (accountOpen = false)}
      onManage={() => {
        accountOpen = false;
        adminOpen = true;
      }}
    />
  {/if}

  {#if adminOpen}
    <AdminPanel onClose={() => (adminOpen = false)} />
  {/if}

  {#if $selectedDay}
    <DayModal iso={$selectedDay} onClose={() => selectedDay.set(null)} />
  {/if}

  <div class="body">
    {#if !$isPhone}
      <VotingsPanel />
    {/if}

    <div class="mainarea">
      {#if !$isPhone}
        <div class="bar">
          <div class="tabs panel">
            {#each wideTabs as tab (tab.view)}
              <button
                class="tab"
                class:active={$filters.view === tab.view}
                onclick={() => setView(tab.view)}
              >
                {$t(tab.key)}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      <!-- range/focus/sort only mean something for the day views -->
      {#if DAY_VIEWS.includes($filters.view)}
        <Controls />
      {/if}

      {#if $filters.view === "calendar"}
        {#if $isPhone}
          <!-- Above the calendar, never below it: the calendar grows as you
               scroll, so anything after it can never be reached on a phone. -->
          <Sidebar />
          <CalendarView />
        {:else}
          <div class="cal-grid">
            <CalendarView />
            <Sidebar />
          </div>
        {/if}
      {:else if $filters.view === "timeline"}
        <TimelineView />
      {:else if $filters.view === "events"}
        <EventsView />
      {:else}
        <VotingsPanel />
      {/if}
    </div>
  </div>
</div>

{#if $isPhone}
  <nav class="bottom-nav" aria-label={$t("nav.views")}>
    {#each TABS as tab (tab.view)}
      <button
        class="nav-btn"
        class:active={$filters.view === tab.view}
        aria-current={$filters.view === tab.view ? "page" : undefined}
        onclick={() => setView(tab.view)}
      >
        <NavIcon kind={tab.view} />
        <span class="nav-label">{$t(tab.key)}</span>
      </button>
    {/each}
  </nav>
{/if}

<style>
  .shell {
    width: min(1720px, 100%);
    margin: 0 auto;
    padding: 16px;
    display: grid;
    gap: 14px;
  }
  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    flex-wrap: wrap;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 800;
    min-width: 0;
  }
  .name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .session {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--muted);
    flex-wrap: wrap;
    min-width: 0;
  }
  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    flex: 0 0 auto;
  }
  .who {
    font-weight: 700;
    color: var(--ink);
    max-width: 22ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* votings rail (left) + main content (right) */
  .body {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
    gap: 14px;
    align-items: start;
  }
  .mainarea {
    display: grid;
    gap: 14px;
    min-width: 0;
  }
  .bar {
    display: flex;
  }
  .tabs {
    display: flex;
    gap: 6px;
    padding: 6px;
    width: fit-content;
  }
  .tab {
    min-height: 38px;
    border: 0;
    border-radius: 12px;
    padding: 0 18px;
    color: var(--muted);
    background: transparent;
    font-weight: 800;
  }
  .tab.active {
    color: var(--btn-fg);
    background: var(--btn);
  }

  /* calendar + stats sidebar (right) */
  .cal-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 14px;
    align-items: start;
  }

  /* stack the votings rail below the main content so the 7-column calendar
     keeps room, and drop the stats sidebar under the calendar */
  @media (max-width: 1200px) {
    .cal-grid {
      grid-template-columns: 1fr;
    }
    .body {
      grid-template-columns: 1fr;
    }
    .mainarea {
      order: 1;
    }
    :global(.body > .votings) {
      order: 2;
    }
  }

  /* ── phone ─────────────────────────────────────────────────────────────────
     Matches PHONE_QUERY in lib/stores.ts, which drives the markup switch above.
     The topbar is pinned so the account sheet is always one tap away, and the
     shell reserves room for the fixed bottom nav. */
  @media (max-width: 760px) {
    .shell {
      --pad: 12px;
      padding: 0 var(--pad) calc(var(--nav-h) + var(--safe-b) + 16px);
      gap: 12px;
    }
    .topbar {
      position: sticky;
      top: 0;
      z-index: var(--z-nav);
      /* bleed to the screen edges so nothing scrolls through the gap beside it */
      margin: 0 calc(-1 * var(--pad));
      border-width: 0 0 1px;
      border-radius: 0 0 20px 20px;
      padding: calc(9px + var(--safe-t)) calc(var(--pad) + var(--safe-r)) 9px
        calc(var(--pad) + var(--safe-l));
      flex-wrap: nowrap;
      gap: 10px;
    }
    .brand .name {
      font-size: 15px;
    }
    .account-btn {
      flex: 0 0 auto;
      display: grid;
      place-items: center;
      width: var(--tap);
      height: var(--tap);
      padding: 0;
      border: 1px solid var(--line);
      border-radius: 999px;
      background: var(--surface);
      color: var(--ink);
    }
    .account-btn .avatar {
      width: 100%;
      height: 100%;
    }
    .account-btn .initial {
      font-size: 17px;
      font-weight: 900;
      line-height: 1;
    }
  }

  /* ── bottom nav ────────────────────────────────────────────────────────────
     Thumb-reachable and always visible: the calendar is an infinite scroller,
     so a tab bar at the top of the document scrolls away and never comes back.
     Sits under popovers (--z-pop) and sheets (--z-modal) by design. */
  .bottom-nav {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--z-nav);
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 2px;
    padding: 5px calc(6px + var(--safe-l)) calc(5px + var(--safe-b))
      calc(6px + var(--safe-r));
    border-top: 1px solid var(--line);
    background: var(--panel);
    backdrop-filter: blur(18px);
  }
  .nav-btn {
    display: grid;
    justify-items: center;
    align-content: center;
    gap: 3px;
    min-height: 50px;
    padding: 4px 2px;
    border: 0;
    border-radius: 14px;
    background: transparent;
    /* --ink, not --muted: the inactive tabs are still primary navigation and
       have to stay readable against the translucent bar in every palette */
    color: var(--ink);
    font-weight: 700;
  }
  .nav-btn.active {
    color: var(--btn-fg);
    background: var(--btn);
  }
  .nav-label {
    font-size: 12px;
    font-weight: 800;
    /* the German labels ("Zeitleiste", "Ereignisse") are the constraint: at
       four tabs on a 320px screen there is ~68px of room per label */
    letter-spacing: -0.01em;
    line-height: 1.1;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
